<script>
import cloneDeep from 'lodash/cloneDeep';
import AdminPanel from '@components/admin-panel/admin-panel.vue';
import CardExerciseMinimal from '@components/cards/card-exercise-minimal.vue';
import ExerciseDetail from '@components/exercise/exercise-detail.vue';
import Layout from '@layouts/main.vue';
import SearchInput from '@components/search-input/search-input.vue';
import UseLargeScreen from '@components/use-large-screen.vue';
import Sidebar from '@components/sidebar/sidebar.vue';
import SplitPane from '@components/panels/split-pane.vue';
import SplitPaneFull from '@components/panels/split-pane-full.vue';
import SubheaderActions from '@components/subheader/subheader-actions.vue';
import { adminPanelMixin } from '@mixins/admin-panel-mixin';
import { exerciseModelMixin } from '@mixins/models/exercise-model-mixin';
import { breakpointsMixin } from '@mixins/breakpoints-mixin.js';
import { helperMixin } from '@mixins/helper-mixin';
import {
  adminPanelComputed,
  adminPanelMethods,
  authComputed,
  exerciseComputed,
  exerciseMethods,
  exerciseDifficultyComputed,
  exerciseDifficultyMethods,
  muscleGroupComputed,
  muscleGroupMethods,
  equipmentComputed,
  equipmentMethods,
} from '@state/helpers';

export default {
  page: {
    title: 'Exercises',
    meta: [{ name: 'description', content: 'The Exercises page.' }],
  },
  components: {
    AdminPanel,
    CardExerciseMinimal,
    ExerciseDetail,
    Layout,
    SearchInput,
    Sidebar,
    SplitPane,
    SplitPaneFull,
    SubheaderActions,
    UseLargeScreen,
  },
  mixins: [adminPanelMixin, exerciseModelMixin, helperMixin, breakpointsMixin],
  data() {
    return {
      initialExerciseList: null,
      filters: {
        owners: [],
        muscles: [],
        equipments: [],
        difficulties: [],
      },
      ownerWorkouts: [],
      weightRange: [20, 70],
      isSearchFocused: false,
      isLoadingExercises: false,
      isFiltering: false,
      searchComponentKey: 0,
      searchOpts: { keys: ['label'] },
      displayLimit: 4,
      initialDisplayLimit: 4,
      maxScrollTop: 0,
      showMoreEquipments: false,
      showMoreMuscleGroups: false,
    };
  },
  computed: {
    ...adminPanelComputed,
    ...authComputed,
    ...exerciseComputed,
    ...exerciseDifficultyComputed,
    ...muscleGroupComputed,
    ...equipmentComputed,
    exercises() {
      if (
        this.filters.owners.length ||
        this.filters.muscles.length ||
        this.filters.equipments.length ||
        this.filters.difficulties.length
      ) {
        let exercises = [];

        // Owners Filter logic
        if (
          this.isTrainer &&
          (this.filters.owners.includes('0') ||
            this.filters.owners.includes(this.trainerId))
        ) {
          // We do owner filter if trainer otherwise don't bother
          const filters = this.filters.owners;
          exercises = this.allExercises.filter((exercise) => {
            if (
              filters.includes(this.trainerId) &&
              exercise.trainer_id === this.trainerId
            ) {
              return exercise;
            }
            if (filters.includes('0') && exercise.trainer_id === '0') {
              return exercise;
            }
          });
        } else {
          exercises = this.allExercises;
        }

        if (this.filters.muscles.length) {
          const filters = this.filters.muscles;
          exercises = exercises.filter((exercise) => {
            const hasMuscleGroups =
              exercise.muscleGroups && exercise.muscleGroups.length;
            if (hasMuscleGroups) {
              const matchesMuscleGroup = filters.some((filter) =>
                exercise.muscleGroups.find(
                  (group) => filter === group.muscleGroup_id
                )
              );
              if (matchesMuscleGroup) {
                return exercise;
              }
            }
          });
        }

        if (this.filters.equipments.length) {
          const filters = this.filters.equipments;
          exercises = exercises.filter((exercise) => {
            const equipments = this.requiredEquipment(exercise);
            const hasEquipment = equipments && equipments.length;
            if (
              filters.includes('Bodyweight') &&
              !exercise.requiredEquipment.weighted
            ) {
              return exercise;
            }
            if (hasEquipment) {
              // AND filter
              const matchEquipment = filters.every((filter) =>
                equipments.find((equipment) => filter === equipment)
              );
              if (matchEquipment) {
                return exercise;
              }
            }
          });
        }
        // Diffculty Level filter
        if (this.filters.difficulties.length) {
          const filters = this.filters.difficulties;
          exercises = exercises.filter((exercise) => {
            const difficulty =
              exercise.exerciseDifficulty && exercise.exerciseDifficulty.label
                ? exercise.exerciseDifficulty.label.toLowerCase()
                : null;
            if (difficulty && filters.includes(difficulty)) {
              return exercise;
            }
          });
        }
        return exercises;
      } else {
        return this.allExercises;
      }
    },
    noResults() {
      return {
        title:
          this.exercises.length === 0 && this.initialExerciseList.length > 0
            ? `We couldn't find any results that match your filter. Try changing your filter. Or reset.`
            : 'No Exercises available.',
      };
    },
    noFilterResult() {
      return (
        this.exercises &&
        this.initialExerciseList &&
        this.exercises.length === 0 &&
        this.initialExerciseList.length > 0
      );
    },
    isTrainer() {
      return (
        this.currentUser &&
        this.currentUser.roles &&
        this.currentUser.roles.isTrainer
      );
    },
    panelContentDetail() {
      return (
        (this.panelContent &&
          this.panelContent.data &&
          this.panelContent.data.details) ||
        (this.panelContent &&
          this.panelContent.data &&
          this.panelContent.data.componentRight &&
          this.panelContent.data.componentRight.showPanelDetail)
      );
    },
    allEquipmentSorted() {
      const index = this.sortingEquipment();
      const sortedEquipments = cloneDeep(this.allEquipment);
      sortedEquipments.splice(index, 0, {
        _id: 'Bodyweight',
        label: 'Bodyweight',
      });
      return sortedEquipments;
    },
  },
  async mounted() {
    this.ownerWorkouts = [
      {
        label: 'My Library',
        id: this.trainerId,
      },
      {
        label: this.$store.getters['organization/organizationTitle'],
        id: '0',
      },
    ];

    if (this.exerciseFilter) {
      for (const filterName in this.exerciseFilter) {
        this.filters[filterName] = cloneDeep(this.exerciseFilter[filterName]);
      }
    }

    this.isLoadingExercises = true;
    if (!this.allExercises.length) {
      this.getAllExerciseDifficulties({
        orgId: this.userOrgId,
        trainerId: '0',
      });
      this.getAllMuscleGroups({ orgId: this.userOrgId, trainerId: '0' });
      this.getExerciseShape({ orgId: this.userOrgId, trainerId: '0' });
      this.getAllEquipment({ orgId: this.userOrgId, trainerId: '0' });
      await this.getAllExercises({ orgId: this.userOrgId, trainerId: '0' });
    }
    this.isLoadingExercises = false;
    this.initialExerciseList = await cloneDeep(this.allExercisesTrueSource);
    window.addEventListener('resize', this.adjustDisplayLimit); // For browser dynamic resizing
    this.adjustDisplayLimit(); // Call on mount first
  },
  destroyed() {
    this.$_clearSelections();
    this.setAllExercises(this.allExercisesTrueSource);
    window.removeEventListener('resize', this.adjustDisplayLimit);
  },
  methods: {
    ...adminPanelMethods,
    ...exerciseMethods,
    ...exerciseDifficultyMethods,
    ...muscleGroupMethods,
    ...equipmentMethods,
    resetfilters() {
      for (const filterName in this.filters) {
        this.filters[filterName] = [];
      }
      this.setAllExercises(this.allExercisesTrueSource);
      if (
        this.$refs &&
        this.$refs.searchInput &&
        this.$refs.searchInput.$el &&
        this.$refs.searchInput.$children.length > 1 &&
        this.$refs.searchInput.$children[1].$el.firstChild
      ) {
        this.$refs.searchInput.$children[1].$el.firstChild.value = null;
      }
    },
    onHidePanel() {
      // Check Changes
      if (this.hasUnsavedExerciseChanges && !this.panelContentDetail) {
        this.$router.app.$bvModal.show('unsaved-changes-modal');
      } else {
        if (
          this.panelContent &&
          this.panelContent.data &&
          this.panelContent.data.componentRight &&
          this.panelContent.data.componentRight.showPanelDetail
        ) {
          this.setHidePanelContentDetail();
        } else {
          this.setSelectedExercise(null);
        }
      }
    },
    async showAdminPanel(component, data = null, size = '') {
      await this.setPanelContent(this.getPanelContent(component, data, size));
      this.setShowAdminPanel();
    },
    async onShowAdminPanel() {
      const exercise = await this.getExerciseModel();
      exercise.trainer_id = this.trainerId;
      exercise.org_id = this.userOrgId;
      await this.setSelectedExercise(exercise);

      const panelContent = {
        panelTitle: 'Create New Exercise',
        componentLeft: {
          name: 'ExerciseDetail',
          selectedExercise: this.selectedExercise,
          isNewExercise: true,
          inPanel: true,
        },
        componentRight: {
          name: null,
        },
      };

      this.showAdminPanel('SplitPaneFull', panelContent, 'split-lg');
    },
    onFilterResults(val) {
      this.setAllExercises(val);
    },
    onFocus(e) {
      if (!e) {
        this.isSearchFocused = true;
      }
      if (e && !e.target.value) {
        this.isSearchFocused = false;
      }
    },
    visibilityChanged(isVisible, entry) {
      // Re-render search conponent when visibility changes on the search input parent
      this.searchComponentKey++;
    },
    requiredEquipment(exercise) {
      const listOfEquipment = [];
      if (
        exercise &&
        exercise.requiredEquipment &&
        exercise.requiredEquipment.weighted
      ) {
        listOfEquipment.push(exercise.requiredEquipment.weighted._id);
      }
      if (
        exercise &&
        exercise.requiredEquipment &&
        exercise.requiredEquipment.unweighted.length
      ) {
        exercise.requiredEquipment.unweighted.map((equipment) =>
          listOfEquipment.push(equipment._id)
        );
      }
      return listOfEquipment;
    },
    adjustDisplayLimit(maxScroll) {
      this.displayLimit = this.initialDisplayLimit; // Set initial limit

      if (
        this.$el.getElementsByClassName('content-wrapper').length &&
        this.$el.getElementsByClassName('card-exercise-minimal').length &&
        this.$el.getElementsByClassName('card-exercise-minimal').length
      ) {
        const containerHeight =
          this.$el.getElementsByClassName('content-wrapper')[0].clientHeight +
          this.$el.getElementsByClassName('content-wrapper')[0].scrollTop;
        const gridWidth = this.$el.getElementsByClassName('grid-cards')[0]
          .clientWidth;
        const cardExerciseHeight =
          this.$el.getElementsByClassName('card-exercise-minimal')[0]
            .clientHeight > 222
            ? this.$el.getElementsByClassName('card-exercise-minimal')[0]
                .clientHeight
            : 222;
        const exerciseCardWidth = this.$el.getElementsByClassName(
          'card-exercise-minimal'
        )[0].clientWidth;

        const countInRow = Math.floor(gridWidth / exerciseCardWidth); // Number of exercises in one row
        const countInColumn = Math.floor(containerHeight / cardExerciseHeight); // Number of rows in the container

        if (containerHeight > cardExerciseHeight) {
          if (maxScroll) {
            // If on scroll
            const moreRowsScrolled =
              Math.round(maxScroll / cardExerciseHeight) + 1; // get how many more rows scrolled and add one to them
            this.displayLimit =
              countInRow * countInColumn + moreRowsScrolled * countInRow;
          } else {
            // When mounted
            this.displayLimit = countInRow * countInColumn;
          }
        }
      }
    },
    onScroll() {
      let scrollContainer = null;
      scrollContainer = this.$el.getElementsByClassName('content-wrapper')[0];
      const scrollTop = scrollContainer.scrollTop;

      if (this.maxScrollTop < scrollTop) {
        this.maxScrollTop = scrollTop - this.maxScrollTop;
      }
      this.adjustDisplayLimit(this.maxScrollTop); // Adjust the limit after passing the number of pixels
    },
    sortingEquipment() {
      const labels = this.allEquipment.map((equipment) => equipment.label);
      labels.push('Bodyweight');
      const sortedArray = labels.sort((a, b) => {
        if (a < b) {
          return -1;
        }
        if (a > b) {
          return 1;
        }
        return 0;
      });
      const index = sortedArray.indexOf('Bodyweight');
      return index;
    },
  },
  watch: {
    filters: {
      deep: true,
      handler: function(newValue) {
        const filterValue = cloneDeep(newValue);
        this.setExercisesFilter(filterValue);
      },
    },
  },
};
</script>

<template>
  <UseLargeScreen v-if="isSmallScreenView" />
  <Layout v-else>
    <AdminPanel
      :title="panelContent ? panelContent.title : ''"
      :size="panelContent ? panelContent.size : ''"
      :split="
        panelContent && panelContent.data && panelContent.data.split === true
      "
      :noPad="true"
      @hide-panel="onHidePanel"
    >
      <component
        v-if="panelContent"
        :is="panelContent.component"
        :content="panelContent"
        :exercise="selectedExercise"
        :inPanel="true"
      />
    </AdminPanel>

    <div class="container-wrapper" v-observe-visibility="visibilityChanged">
      <Sidebar v-if="!isLoadingExercises">
        <h5 class="sidebar-section-title">Filter Exercises</h5>
        <div v-if="isTrainer" class="sidebar-section">
          <div class="sidebar-section-container">
            <legend class="sidebar-subtitle">Library</legend>
            <div class="sidebar-section-container">
              <BaseCheckboxModel
                ref="owner_checkBox"
                itemRef="ownerWorkouts"
                v-for="(item, i) in ownerWorkouts"
                :item="item"
                :itemIndex="i"
                :key="i"
                :value="item.id"
                v-model="filters.owners"
              />
            </div>
          </div>
        </div>

        <div v-if="allMuscleGroups.length" class="sidebar-section">
          <div class="sidebar-section-container">
            <legend class="sidebar-subtitle">Muscle Group</legend>
            <div class="sidebar-section-container">
              <BaseCheckboxModel
                ref="muscleGroups_checkBox"
                itemRef="muscleGroups"
                v-for="(item, i) in allMuscleGroups"
                :item="item"
                :itemIndex="i"
                :key="i"
                :value="item._id"
                v-model="filters.muscles"
                :style="
                  `display: ${
                    !showMoreMuscleGroups && i >= 4 ? 'none' : 'inline-flex'
                  }`
                "
              />
              <a
                class="link-more"
                @click="showMoreMuscleGroups = !showMoreMuscleGroups"
              >
                <span v-if="!showMoreMuscleGroups">More...</span>
                <span v-else>Less...</span>
              </a>
            </div>
          </div>
        </div>
        <div v-if="allEquipmentSorted.length" class="sidebar-section">
          <div class="sidebar-section-container">
            <legend class="sidebar-subtitle">Equipment</legend>
            <div class="sidebar-section-container">
              <BaseCheckboxModel
                ref="equipments_checkBox"
                itemRef="equipments"
                v-for="(item, i) in allEquipmentSorted"
                :item="item"
                :itemIndex="i"
                :key="i"
                :value="item._id"
                v-model="filters.equipments"
                :style="
                  `display: ${
                    !showMoreEquipments && parseInt(i) >= 5
                      ? 'none'
                      : 'inline-flex'
                  }`
                "
              />
              <a
                class="link-more"
                @click="showMoreEquipments = !showMoreEquipments"
              >
                <span v-if="!showMoreEquipments">More...</span>
                <span v-else>Less...</span>
              </a>
            </div>
          </div>
        </div>

        <div v-if="allExerciseDifficulties.length" class="sidebar-section">
          <div class="sidebar-section-container">
            <legend class="sidebar-subtitle">Level</legend>
            <div class="sidebar-section-container">
              <BaseCheckboxModel
                ref="difficulty_checkBox"
                itemRef="difficulty"
                v-for="(item, i) in allExerciseDifficulties"
                :item="item"
                :itemIndex="i"
                :id="item._id"
                :key="i"
                :value="item.label.toLowerCase()"
                v-model="filters.difficulties"
              />
            </div>
          </div>
        </div>
      </Sidebar>

      <div class="content-wrapper" @scroll="onScroll">
        <SubheaderActions v-if="!isLoadingExercises">
          <SearchInput
            :key="searchComponentKey"
            ref="searchInput"
            :class="{ focus: isSearchFocused }"
            :dark="true"
            :list="initialExerciseList"
            :options="searchOpts"
            @filter-results="onFilterResults"
            @focus-filter-input="onFocus"
            @focusout-filter-input="onFocus"
          />
          <BaseButton
            type="button"
            class="btn-primary btn-round"
            @click="onShowAdminPanel"
            v-if="
              (currentUser.roles && currentUser.roles.isAdmin) ||
                currentUser.roles.isTrainer
            "
          >
            <img src="@assets/images/icon-curl.svg" class="invert-color" />
            Create New Exercise
          </BaseButton>
        </SubheaderActions>

        <div
          class="grid-cards"
          v-if="!isLoadingExercises && exercises.length !== 0"
        >
          <template v-for="(item, i) in exercises">
            <CardExerciseMinimal
              ref="cardExerciseMinimal"
              v-if="i < displayLimit"
              :key="i"
              :exercise="item"
            />
          </template>
        </div>
        <div
          v-if="
            exercises &&
              exercises.length === 0 &&
              initialExerciseList &&
              initialExerciseList.length === 0 &&
              !isLoadingExercises
          "
          class="empty-message--large"
        >
          <h1>{{ noResults.title }}</h1
          ><br />
        </div>
        <div
          v-if="
            exercises &&
              exercises.length === 0 &&
              initialExerciseList &&
              initialExerciseList.length > 0 &&
              !isLoadingExercises
          "
          class="empty-state-container"
        >
          <h2>We couldn’t find what you’re looking for.</h2>
          <p
            >This exercise does not exist in the exercise library. If you want
            to use this exercise, you will need create it.</p
          >
          <div class="actions">
            <BaseButton
              v-if="
                exercises &&
                  initialExerciseList &&
                  exercises.length === 0 &&
                  initialExerciseList.length > 0 &&
                  !isLoadingExercises
              "
              type="button"
              class="btn-secondary btn-round"
              @click="resetfilters"
            >
              Clear Filter
            </BaseButton>
            <BaseButton
              v-if="
                exercises &&
                  initialExerciseList &&
                  exercises.length === 0 &&
                  initialExerciseList.length > 0 &&
                  !isLoadingExercises
              "
              type="button"
              class="btn-primary btn-round"
              @click="onShowAdminPanel"
            >
              <img class="invert-color" src="@assets/images/icon-curl.svg" />
              Create New
            </BaseButton>
          </div>
        </div>
        <div class="loading-wrapper" v-if="isLoadingExercises || isFiltering">
          <BaseLoadingSpinner />
        </div>
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown

.empty-state-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 8px rgba($color-black-wo-rgb, 0.08);
  display: flex;
  flex-direction: column;
  margin-bottom: 30vh;
  margin-left: auto;
  margin-right: auto;
  margin-top: 25vh;
  padding: 6rem;
  text-align: center;
  width: 50%;

  p {
    margin-top: 2rem;
  }

  .actions {
    align-items: center;
    display: flex;
    justify-content: center;
    margin-top: 2rem;

    ::v-deep .button {
      height: 3.7rem;
      padding-bottom: 0.2rem;
      padding-top: 0.2rem;
      white-space: nowrap;
    }

    ::v-deep .button.btn-secondary {
      margin-right: 10px;
    }
  }
}

.loading-wrapper {
  align-items: center;
  display: flex;
  justify-content: center;
  margin-top: 35vh;
  width: 100%;

  ::v-deep .loading {
    height: 4rem;
    width: 4rem;
  }
}

.reset-filter {
  align-items: center;
  display: flex;
  justify-content: center;
  width: 100%;
}

.grid-cards {
  @include grid-cards(3, 23);

  .noResults {
    font-size: 15px;
    font-weight: bold;
    margin: auto;
    padding: 10px;
    text-align: center;

    button {
      margin: 10px;
    }
  }
}

.sidebar-section-container {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  justify-content: center;

  a {
    color: $color-gray;
    font-size: 1.1rem;
    margin-left: 2.4rem;
  }
}

.sidebar-section-title {
  color: $color-heading-text;
  font-family: $base-font-family;
  font-size: 1.2rem;
  font-weight: 900;
  letter-spacing: 0.1px;
  line-height: 2.2rem;
  margin-bottom: 1rem;
  text-transform: uppercase;
}

// stylelint-disable selector-max-type
label {
  position: relative;
}

.check-box-title {
  font-family: $base-font-family;
  font-size: 1.2rem;
  font-weight: 900;
  letter-spacing: 0.2px;
  line-height: 2rem;
  margin-left: 7px;
  text-transform: capitalize;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.type-opts-container {
  display: inline-flex;
  justify-content: center;
  margin-bottom: 0.5rem;

  label {
    margin-bottom: 0.2rem;
  }
}
.check-box {
  background-color: $color-light-gray;
  border-radius: 10%;
  cursor: pointer;
  display: inline-block;
  height: 14px;
  transition: all linear 0.3s;
  width: 14px;

  &::after {
    border-bottom: 2px solid white;
    border-right: 2px solid white;
    content: '';
    height: 9px;
    left: 5px;
    opacity: 0.6;
    position: absolute;
    top: 2.5px;
    transform: rotate(45deg);
    visibility: hidden;
    width: 5px;
  }
}

input {
  display: none;

  &:checked ~ .check-box {
    background: $color-action;
    border: unset;

    &::after {
      visibility: visible;
    }
  }
}

.input-filter {
  text-align-last: center;
}

.search-input-wrapper {
  ::v-deep path {
    fill: $color-action;
  }

  ::v-deep .input {
    border: 1px solid $color-light-gray;
  }

  &.focus {
    ::v-deep path {
      fill: $color-heading-text;
    }

    ::v-deep .input {
      border: 1px solid $color-action;
    }
  }
}

@media screen and (max-width: 888px) {
  .content-wrapper {
    .btn-primary {
      font-size: 1.1rem;
      padding-left: 1rem;
      padding-right: 1rem;
      width: 17rem;
    }

    img {
      width: 1.4rem;
    }
  }
}
</style>
